import { unwrap } from '@hive/api';
import {
  ExportMenu,
  Icon,
  KpiStrip,
  Loading,
  Meter,
  Modal,
  PageHelpButton,
  Panel,
  Pill,
  ProblemBox,
  useHelpScreen,
  useLoad,
  useStatusBar,
  useToast,
  type AttentionItem,
  type AttentionTone,
  type ExportSnapshot,
  type KpiStripItem,
  type StatusItem,
} from '@hive/ui';
import { lazy, Suspense, useLayoutEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useI18nP, useT, type PlatformKey } from '../../i18n';
import { readRecent } from '../../lib/recent';
import { screenRoute, wizardRoute } from '../../lib/routes';
import { useSession } from '../../lib/session';
import { profileName } from '../../lib/wizards';
import { rcaApi } from '../../lib/slarca';
import { RootCausesCard } from '../sla/RootCausesCard';
import { useValueLabel } from '../inventory/common';
import { DataAge } from '../../lib/sweep3';

interface Card {
  key: string;
  title?: string;
  available: boolean;
  phase?: string;
  value?: number;
  severity?: string;
  screen?: string;
  filter?: Record<string, string>;
}
interface Step {
  key: string;
  wizard?: string;
  screen?: string;
  phase: string;
  available: boolean;
  done: boolean;
}
interface HomeData {
  cards: Card[];
  firstSteps?: { hidden: boolean; startedAt?: string; steps: Step[]; done: number; total: number; minutesToFirstInventory?: number | null };
}
/** Cobertura de agentes (GET /api/v1/agents/coverage). */
interface Coverage {
  assets?: number;
  withAgent?: number;
  online?: number;
  late?: number;
  offline?: number;
  collectors?: number;
  certsExpiringIn30Days?: number;
}

const DashboardView = lazy(() => import('../reports/DashboardsScreen').then((m) => ({ default: m.DashboardView })));
const ReadyDashboardView = lazy(() => import('../reports/DashboardsScreen').then((m) => ({ default: m.ReadyDashboardView })));

const INVENTORY = new Set(['assetsByType', 'agentCoverage', 'openAlerts', 'changes24h', 'agentlessMachines']);
/** Cartão sintético do total de ativos (soma dos "assets:<tipo>"): entra no Personalizar como os outros. */
const TOTAL = 'assetsTotal';
/** Problemas que ainda vão acontecer (prazo conhecido): entram como "prevista", antes dos abertos. */
const PREDICTED: Record<string, string> = { apiTokensExpiring: '15 d' };
const TONE_ORDER: AttentionTone[] = ['pred', 'crit', 'warn', 'info', 'ok'];

/** Cor da etiqueta pela severidade do cartão. */
function toneOf(c: Card): AttentionTone {
  if (PREDICTED[c.key]) return 'pred';
  if (c.severity === 'error' || c.severity === 'critical') return 'crit';
  if (c.severity === 'attention' || c.severity === 'warning') return 'warn';
  return 'info';
}

/**
 * T-03 — Início (HU-004), no layout A + C (tela de referência para as outras): saudação, faixa de indicadores,
 * "Atenção agora" (previstas primeiro, depois as abertas), agentes online, "Continuar de onde parou" e o rodapé de
 * status. Respeita o perfil ativo; o que ainda não tem dado aparece como tal, sem números inventados.
 */
export function Home() {
  useHelpScreen('home');
  const t = useT();
  const { fmt, has } = useI18nP();
  const s = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const home = useLoad(async () => (await unwrap(s.api.GET('/api/v1/home'))) as unknown as HomeData, [s.api, s.me.activeProfile?.id]);
  const layoutL = useLoad(() => unwrap(s.api.GET('/api/v1/home/layout')).catch(() => ({ order: [], hidden: [] })) as Promise<Layout>, [s.api]);
  const charts = useLoad(() => unwrap(s.api.GET('/api/v1/home/charts')).catch(() => ({})) as unknown as Promise<Charts>, [s.api]);
  const coverage = useLoad(
    () => (s.can('assets:read') ? (unwrap(s.api.GET('/api/v1/agents/coverage')).catch(() => null) as Promise<Coverage | null>) : Promise.resolve(null)),
    [s.api],
  );
  // painel lateral: frota por local e causas carregam junto com o resto (antes só depois do primeiro desenho, e cada
  // cartão que chegava empurrava os de baixo — CLS do Início, QA 5 · Q5-4)
  const sites = useLoad(
    () => (unwrap(s.api.GET('/api/v1/geo/sites' as never, { params: { query: { limit: 12 } } } as never)) as unknown as Promise<{ items?: FleetSite[] }>).catch(() => null),
    [s.api],
  );
  const canCauses = s.can('screen:monitor.causes:view');
  const causes = useLoad(() => (canCauses ? rcaApi.summary(s, 30).catch(() => null) : Promise.resolve(null)), [s.api, canCauses]);
  const [customizing, setCustomizing] = useState(false);
  const [params] = useSearchParams();
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  // antes do desenho: a idade dos dados entra no mesmo quadro que os dados (sem empurrar os botões do cabeçalho)
  useLayoutEffect(() => {
    if (home.data) setLoadedAt(Date.now());
  }, [home.data]);
  const layout = layoutL.data ?? { order: [], hidden: [] };
  const arrange = <T,>(items: T[], key: (x: T) => string) =>
    items
      .filter((x) => !(layout.hidden ?? []).includes(key(x)))
      .map((x, i) => ({ x, i, o: (layout.order ?? []).indexOf(key(x)) }))
      .sort((a, b) => (a.o < 0 ? 1000 + a.i : a.o) - (b.o < 0 ? 1000 + b.i : b.o))
      .map((y) => y.x);

  const label = (key: string, part: 'title' | 'foot') => {
    // Pesquisas salvas da lista de ativos (T-10) trazem o nome dado pela pessoa.
    if (key.startsWith('search:')) return part === 'title' ? cards.find((c) => c.key === key)?.title ?? key : t('home.card.search.foot');
    // Cartões de inventário por tipo vêm como "assets:<tipo>" (F1).
    if (key.startsWith('assets:')) {
      const kind = key.slice(7);
      const kk = `home.kind.${kind}`;
      const k2 = `kind.${kind}`;
      const name = has(kk) ? t(kk as PlatformKey) : has(k2) ? t(k2 as PlatformKey) : kind;
      return part === 'title' ? t('home.card.assetsKind.title', { kind: name }) : '';
    }
    if (key === TOTAL) return part === 'title' ? t('home.kpi.assets') : '';
    // economia da otimização: o título leva a moeda da empresa (vem no campo title do cartão)
    if (key === 'rightsizingSavings' && part === 'title') return t('home.card.rightsizingSavings.title', { currency: cards.find((c) => c.key === key)?.title ?? '' });
    const k = `home.card.${key}.${part}`;
    return has(k) ? t(k as PlatformKey) : key;
  };
  const cards = home.data?.cards ?? [];
  const isInventory = (c: Card) => c.key.startsWith('assets:') || c.key.startsWith('search:') || INVENTORY.has(c.key);
  const chartKeys = arrange(CHART_KEYS.filter((k) => (charts.data as Record<string, unknown> | undefined)?.[k]), (k) => k);

  // indicadores: o total de ativos (os tipos mais comuns no rodapé), depois os demais de inventário e as pesquisas salvas
  const kinds = cards.filter((c) => c.key.startsWith('assets:') && c.available).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  const totalAssets = kinds.reduce((n, c) => n + (c.value ?? 0), 0);
  const kpiCards: Card[] = [
    ...(kinds.length || s.can('assets:read') ? [{ key: TOTAL, available: kinds.length > 0, value: totalAssets, screen: 't10' } satisfies Card] : []),
    ...cards.filter((c) => isInventory(c) && !c.key.startsWith('assets:')),
  ];
  const kpis: KpiStripItem[] = arrange(kpiCards, (c) => c.key).map((c) => {
    const title = label(c.key, 'title');
    const foot =
      c.key === TOTAL
        ? kinds
            .slice(0, 2)
            .map((k) => `${fmt.number(k.value ?? 0)} ${label(k.key, 'title').toLowerCase()}`)
            .join(' · ')
        : c.available
          ? label(c.key, 'foot')
          : t('home.unavailable');
    const n = c.value ?? 0;
    return {
      id: c.key,
      label: title,
      value: c.available ? fmt.number(n) : '—',
      foot,
      tone: !c.available || n === 0 ? undefined : c.key === 'openAlerts' ? (c.severity === 'critical' ? 'crit' : 'warn') : c.key === 'agentlessMachines' ? 'warn' : undefined,
      to: c.available ? screenRoute(c.screen, c.filter) : undefined,
      ariaLabel: c.available ? `${title}: ${fmt.number(n)}` : `${title}: ${t('home.notYet')}`,
    };
  });

  // atenção agora: previstas primeiro (prazo conhecido), depois críticos, atenção e informação; só o que tem valor
  const attnCards = arrange(
    cards.filter((c) => c.available && (c.value ?? 0) > 0 && (!isInventory(c) || c.key === 'openAlerts' || c.key === 'agentlessMachines')),
    (c) => c.key,
  );
  const attn: AttentionItem[] = attnCards.map((c) => {
    const tone = toneOf(c);
    const to = screenRoute(c.screen, c.filter);
    return {
      id: c.key,
      tone,
      tag: tone === 'pred' ? t('home.tag.predIn', { when: PREDICTED[c.key] ?? '' }) : t(`home.tag.${tone === 'ok' ? 'info' : tone}` as PlatformKey),
      title: `${label(c.key, 'title')} · ${fmt.number(c.value ?? 0)}`,
      where: label(c.key, 'foot'),
      to,
      action: { label: t('home.attn.open'), to },
    };
  });
  const certs = coverage.data?.certsExpiringIn30Days ?? 0;
  if (certs > 0)
    attn.push({
      id: 'agentCerts',
      tone: 'pred',
      tag: t('home.tag.predIn', { when: '30 d' }),
      title: `${t('home.certs.title')} · ${fmt.number(certs)}`,
      where: t('home.certs.where'),
      to: '/agents',
      action: { label: t('home.attn.open'), to: '/agents' },
    });
  attn.sort((a, b) => TONE_ORDER.indexOf(a.tone) - TONE_ORDER.indexOf(b.tone));

  // rodapé de status: coletores e agentes online, e a idade dos dados do Início
  const cov = coverage.data;
  const statusItems: StatusItem[] = cov
    ? [
        { id: 'collectors', label: t('home.status.collectors', { count: cov.collectors ?? 0 }), tone: (cov.collectors ?? 0) > 0 ? 'ok' : 'warn', to: '/agents' },
        { id: 'agents', label: t('home.status.agents', { count: cov.online ?? 0 }), tone: (cov.offline ?? 0) > 0 ? 'warn' : 'ok', to: '/agents' },
      ]
    : [];
  useStatusBar(statusItems, { updatedAt: loadedAt, what: t('home.status.what') });

  const setHidden = async (hidden: boolean) => {
    try {
      await unwrap(s.api.PUT('/api/v1/onboarding', { body: { hidden } } as never));
      home.reload();
      if (hidden) toast(t('home.fs.hiddenToast'), { kind: 'info' });
    } catch {
      toast(t('home.fs.cantHide'), { kind: 'crit' });
    }
  };

  // a pessoa escolheu um painel como página inicial: o Início abre nele (com o caminho de volta à página padrão)
  if (layoutL.loading) return <Loading />;
  if (layout.dashboard && params.get('classic') !== '1') {
    const d = layout.dashboard;
    return (
      <>
        <p className="hint small row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {t('home.dash.note')} <Link to="/?classic=1">{t('home.dash.classic')}</Link>
        </p>
        <Suspense fallback={<Loading />}>{d.startsWith('ready:') ? <ReadyDashboardView fixedKey={d.slice(6)} /> : <DashboardView fixedId={d} />}</Suspense>
      </>
    );
  }
  // estrutura estável desde o primeiro desenho: a saudação com a frase de estado, os indicadores, a fila (que inclui os
  // certificados da cobertura) e os gráficos entram juntos; o painel lateral entra inteiro quando os cartões dele chegam
  // (nada entra por cima do que já está na tela)
  const mainReady = !!home.data && !coverage.loading && !charts.loading;
  const sideReady = mainReady && !sites.loading && !causes.loading;
  if (!mainReady && !home.error) return <Loading />;
  // recorte do Início para o menu Exportar (HTML interativo com os cartões, os tipos de ativo e a lista de atenção)
  const snapshot = (): ExportSnapshot => ({
    title: t('home.title'),
    filters: { [t('menu.activeProfile')]: profileName(t, has, s.me.activeProfile) },
    kind: 'dashboard',
    data: {
      cards: arrange(kpiCards, (c) => c.key).map((c) => ({
        label: label(c.key, 'title'),
        value: c.available ? (c.value ?? 0) : '—',
        foot: c.key === TOTAL ? undefined : c.available ? label(c.key, 'foot') : t('home.unavailable'),
      })),
      charts: kinds.length ? [{ title: t('home.kpi.assets'), items: kinds.map((k) => ({ label: label(k.key, 'title'), value: k.value ?? 0 })) }] : [],
      lists: [{ title: t('home.attn.title'), items: attn.map((a) => ({ tag: String(a.tag), tone: a.tone === 'ok' ? 'info' : a.tone, title: String(a.title), where: a.where ? String(a.where) : undefined })) }],
    },
  });
  const hour = new Date().getHours();
  const first = (s.me.name || s.me.email || '').split(/[\s@]+/)[0] ?? '';
  const greet = t(hour < 12 ? 'home.greet.morning' : hour < 18 ? 'home.greet.afternoon' : 'home.greet.evening', { name: first });
  const fs = home.data?.firstSteps;
  // frase de estado (como no protótipo): estável, estável com falha prevista, ou N críticos pedindo atenção
  const nCrit = attn.filter((a) => a.tone === 'crit').length;
  const nPred = attn.filter((a) => a.tone === 'pred').length;
  const stateLine = !home.data ? null : nCrit ? (
    <>
      {t('sw3.home.state.critLead')} <span style={{ color: 'var(--crit)' }}>{t('sw3.home.state.crit', { count: nCrit })}</span>
    </>
  ) : nPred ? (
    <>
      {t('sw3.home.state.okLead')} <span style={{ color: 'var(--ok)' }}>{t('sw3.home.state.stable')}</span>
      {t('sw3.home.state.but')} <span style={{ color: 'var(--warn)' }}>{t('sw3.home.state.pred', { count: nPred })}</span>
    </>
  ) : (
    <>
      {t('sw3.home.state.okLead')} <span style={{ color: 'var(--ok)' }}>{t('sw3.home.state.stable')}</span>.
    </>
  );
  return (
    <>
      <div className="home-greet">
        <div className="xs muted" style={{ flexBasis: '100%', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {t('sw3.home.eyebrow', { date: fmt.date(new Date()) })}
        </div>
        <h1 tabIndex={-1} data-page-title style={{ maxWidth: 900 }}>
          {greet}
          {stateLine && <span style={{ fontWeight: 500 }}> {stateLine}</span>}
        </h1>
        <div className="ph-actions">
          <DataAge at={loadedAt} what={t('home.status.what')} />
          <ExportMenu screen="home" kind="dashboard" snapshot={snapshot} />
          <button type="button" className="btn" onClick={() => setCustomizing(true)}>
            <Icon name="edit" size="sm" /> {t('home.customize')}
          </button>
          <PageHelpButton />
        </div>
      </div>
      {customizing && (
        <Customize
          items={[
            ...kpiCards.map((c) => ({ key: c.key, label: label(c.key, 'title') })),
            ...cards.filter((c) => !isInventory(c)).map((c) => ({ key: c.key, label: label(c.key, 'title') })),
            ...CHART_KEYS.map((k) => ({ key: k, label: t(`home.chart.${k}` as PlatformKey) })),
          ]}
          layout={layout}
          onClose={(saved) => {
            setCustomizing(false);
            if (saved) layoutL.reload();
          }}
        />
      )}
      {home.error ? (
        <ProblemBox error={home.error} actions={<button type="button" className="btn" onClick={home.reload}>{t('ui.retry')}</button>} />
      ) : (
        <div className="stack">
          {kpis.length > 0 && <KpiStrip label={t('home.kpis')} items={kpis} />}
          <div className="home-split">
            <div className="home-main">
              <PriorityQueue items={attn} link={s.can('screen:home.decisions:view') ? { label: t('home.attn.queue'), to: '/decisions' } : undefined} />
              {chartKeys.length > 0 && charts.data && (
                <section aria-labelledby="home-charts">
                  <div className="sec-head" style={{ marginBottom: 10 }}>
                    <h2 id="home-charts">{t('home.charts')}</h2>
                  </div>
                  <div className="grid-2">
                    {chartKeys.map((k) => (
                      <HomeChart key={k} k={k} data={charts.data!} />
                    ))}
                  </div>
                </section>
              )}
            </div>
            <div className="home-side" aria-busy={!sideReady}>
              {sideReady && (
                <>
                  <FleetBySite sites={sites.data?.items ?? []} />
                  {cov && <AgentsOnline cov={cov} />}
                  {canCauses && <RootCausesCard summary={causes.data ?? null} />}
                  <RecentScreens />
                  {fs && !fs.hidden ? (
                    <Panel
                      title={t('home.fs.title')}
                      actions={
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setHidden(true)}>
                          {t('home.fs.hide')}
                        </button>
                      }
                    >
                      <div className="stack-sm">
                        <div className="row between">
                          <b>{t('home.fs.progress', { done: fs.done, total: fs.total })}</b>
                          <span className="muted small">{t('home.fs.goal')}</span>
                        </div>
                        <Meter value={fs.done} max={fs.total} tone="ok" label={t('home.fs.title')} />
                        <ol className="checklist">
                          {fs.steps.map((st, i) => {
                            const nextKey = fs.steps.find((x) => !x.done && x.available)?.key;
                            return (
                              <li key={st.key} className={st.done ? 'done' : st.key === nextKey ? 'next' : ''}>
                                <span className="ck-mark" aria-hidden="true">
                                  {st.done ? '✓' : i + 1}
                                </span>
                                <div className="min0">
                                  <div className="ck-title">
                                    {label(`fs.${st.key}`, 'title')} {st.done && <Pill tone="ok">{t('home.fs.done')}</Pill>}
                                    {!st.done && st.key === nextKey && <Pill tone="accent">{t('home.fs.next')}</Pill>}
                                  </div>
                                  <div className="muted small">{label(`fs.${st.key}`, 'foot')}</div>
                                </div>
                                {st.available ? (
                                  <button type="button" className={st.key === nextKey ? 'btn btn-sm btn-primary' : 'btn btn-sm'} onClick={() => navigate(st.wizard ? wizardRoute(st.wizard, undefined) : screenRoute(st.screen))}>
                                    {st.done ? t('home.fs.again') : t('home.fs.open')}
                                  </button>
                                ) : (
                                  <span className="xs muted">{t('home.unavailable')}</span>
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    </Panel>
                  ) : (
                    <Panel title={t('home.now.title')}>
                      <div className="stack-sm">
                        {fs?.hidden && (
                          <button type="button" className="btn btn-sm" style={{ justifySelf: 'start' }} onClick={() => setHidden(false)}>
                            {t('home.fs.show')}
                          </button>
                        )}
                        <NowList />
                      </div>
                    </Panel>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Fila de prioridade (protótipo da plataforma): uma linha por item com a posição, o que é, onde, o prazo (falha
 * prevista: "~15 d"; aberto: "agora") e a ação. Previstas e críticos primeiro (a ordem vem de quem chama).
 */
function PriorityQueue({ items, link }: { items: AttentionItem[]; link?: { label: string; to: string } }) {
  const t = useT();
  return (
    <section className="mini-panel" aria-labelledby="home-queue">
      <div className="row between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h2 id="home-queue">{t('sw3.home.queue')}</h2>
        <span className="xs muted grow">{t('sw3.home.queueMeta')}</span>
        {link && (
          <Link className="small" to={link.to}>
            {link.label}
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>
          {t('home.attention.none')}
        </p>
      ) : (
        <ol className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={t('sw3.home.queue')}>
          {items.map((a, i) => {
            const pred = a.tone === 'pred';
            const due = pred ? String(a.tag).replace(/^[^~\d]*/, '') || String(a.tag) : t('sw3.home.dueNow');
            return (
              <li key={a.id} className="row" style={{ gap: 12, alignItems: 'center', padding: '8px 0', borderTop: i ? '1px solid var(--line-soft)' : undefined, flexWrap: 'wrap' }}>
                <span className="mono small muted" aria-hidden="true" style={{ width: 20, textAlign: 'right' }}>
                  {i + 1}
                </span>
                <div className="min0 grow" style={{ flex: '1 1 220px' }}>
                  <div className="small" style={{ fontWeight: 600 }}>
                    {a.title}
                  </div>
                  {a.where && <div className="xs muted">{a.where}</div>}
                </div>
                <div style={{ textAlign: 'right', minWidth: 72 }}>
                  <div className="xs muted">{t('sw3.home.due')}</div>
                  <div className="mono small" style={{ color: a.tone === 'crit' ? 'var(--crit)' : pred || a.tone === 'warn' ? 'var(--warn)' : undefined, fontWeight: 600 }}>
                    {due}
                  </div>
                </div>
                {a.action?.to ? (
                  <Link className={`btn btn-sm${i === 0 ? ' btn-primary' : ''}`} to={a.action.to}>
                    {a.action.label}
                  </Link>
                ) : a.to ? (
                  <Link className="btn btn-sm" to={a.to}>
                    {t('home.attn.open')}
                  </Link>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** Frota por local: cada local com o estado (pelo pior), ativos e alertas abertos; some sem locais ou sem permissão. */
function FleetBySite({ sites }: { sites: FleetSite[] }) {
  const t = useT();
  const { fmt } = useI18nP();
  const list = sites.filter((x) => x.assets > 0).sort((a, b) => SITE_ORDER.indexOf(a.state) - SITE_ORDER.indexOf(b.state) || b.assets - a.assets);
  if (!list.length) return null;
  return (
    <section className="mini-panel" aria-labelledby="home-fleet">
      <div className="row between">
        <h2 id="home-fleet">{t('sw3.home.fleet')}</h2>
        <Link className="small" to="/geo">
          {t('sw3.home.fleetMap')}
        </Link>
      </div>
      <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {list.slice(0, 6).map((x) => (
          <li key={x.id} className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: SITE_COLOR[x.state] ?? 'var(--muted)', flex: 'none' }} />
            <Link className="small grow min0" to={`/geo?site=${encodeURIComponent(x.id)}`} title={x.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {x.name}
            </Link>
            <span className="xs muted mono">{t('sw3.home.fleetAssets', { count: x.assets, n: fmt.number(x.assets) })}</span>
            {x.alertsOpen > 0 && <Pill tone={x.alertsCritical ? 'crit' : 'warn'}>{t('sw3.home.fleetAlerts', { count: x.alertsOpen, n: fmt.number(x.alertsOpen) })}</Pill>}
          </li>
        ))}
      </ul>
    </section>
  );
}
type FleetSite = { id: string; name: string; state: string; assets: number; alertsOpen: number; alertsCritical: number };
const SITE_ORDER = ['down', 'degraded', 'unknown', 'ok'];
const SITE_COLOR: Record<string, string> = { ok: 'var(--ok)', degraded: 'var(--warn)', down: 'var(--crit)', unknown: 'var(--muted)' };

/** Agentes agora: barra empilhada (online, atrasados, sem contato) e o resumo em texto. */
function AgentsOnline({ cov }: { cov: Coverage }) {
  const t = useT();
  const { fmt } = useI18nP();
  const online = cov.online ?? 0;
  const late = cov.late ?? 0;
  const offline = cov.offline ?? 0;
  const total = online + late + offline;
  const pct = (n: number) => (total ? `${((100 * n) / total).toFixed(1)}%` : '0%');
  return (
    <section className="mini-panel" aria-labelledby="home-agents">
      <h2 id="home-agents">{t('home.agents.title')}</h2>
      {total ? (
        <>
          <div className="agents-bar" role="img" aria-label={t('home.agents.bar', { online: fmt.number(online), late: fmt.number(late), offline: fmt.number(offline) })}>
            <span className="ab-online" style={{ width: pct(online) }} />
            <span className="ab-late" style={{ width: pct(late) }} />
            <span className="ab-off" style={{ width: pct(offline) }} />
          </div>
          <div className="foot-note">{t('home.agents.now', { online: fmt.number(online), total: fmt.number(total), late: fmt.number(late), offline: fmt.number(offline) })}</div>
        </>
      ) : (
        <div className="foot-note">{t('home.agents.none')}</div>
      )}
    </section>
  );
}

/** "Continuar de onde parou": as últimas telas abertas neste navegador. */
function RecentScreens() {
  const t = useT();
  const { lang } = useI18nP();
  const [list] = useState(() => readRecent().slice(0, 4));
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto', style: 'short' });
  const when = (at: number) => {
    const m = Math.round((Date.now() - at) / 60000);
    if (m < 60) return rtf.format(-m, 'minute');
    const h = Math.round(m / 60);
    if (h < 24) return rtf.format(-h, 'hour');
    return rtf.format(-Math.round(h / 24), 'day');
  };
  return (
    <section className="mini-panel" aria-labelledby="home-recent">
      <h2 id="home-recent">{t('home.recent.title')}</h2>
      {list.length ? (
        <ul className="recent-list">
          {list.map((r) => (
            <li key={r.path}>
              <Link to={r.path}>
                <span>{r.label}</span>
                <span className="when">{when(r.at)}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="foot-note">{t('home.recent.none')}</div>
      )}
    </section>
  );
}

/** Atalhos honestos da F0: o que já dá para fazer hoje, conforme o perfil. */
function NowList() {
  const t = useT();
  const s = useSession();
  const items: Array<[string, string, string]> = [];
  if (s.can('users:admin')) items.push([t('home.now.invite'), t('home.now.inviteDesc'), '/users?invite=1']);
  if (s.can('auth:admin')) items.push([t('home.now.sso'), t('home.now.ssoDesc'), '/users/auth']);
  if (s.can('integrations:read')) items.push([t('home.now.integrations'), t('home.now.integrationsDesc'), '/integrations']);
  if (s.can('api:admin')) items.push([t('home.now.api'), t('home.now.apiDesc'), '/integrations/api']);
  items.push([t('home.now.help'), t('home.now.helpDesc'), '/help']);
  return (
    <div className="list">
      {items.map(([title, desc, to]) => (
        <Link key={to} to={to} className="list-item" style={{ textDecoration: 'none' }}>
          <span className="t">{title}</span>
          <Icon name="chevR" size="sm" />
          <span className="m">{desc}</span>
        </Link>
      ))}
    </div>
  );
}

type Layout = { order?: string[]; hidden?: string[]; dashboard?: string };
type Row = Record<string, string | number | null>;
type Charts = Partial<Record<'alertsPerDay' | 'changesPerDay' | 'assetsByOs' | 'vulnsBySeverity', Row[]>>;
const CHART_KEYS = ['alertsPerDay', 'changesPerDay', 'assetsByOs', 'vulnsBySeverity'] as const;

/** Gráfico simples em SVG: colunas por dia (empilhadas por severidade) ou barras por categoria. */
function HomeChart({ k, data }: { k: (typeof CHART_KEYS)[number]; data: Charts }) {
  const t = useT();
  const { fmt, lang } = useI18nP();
  const vl = useValueLabel();
  const rows = data[k] ?? [];
  const name = (v: string) => {
    const x = vl(k === 'vulnsBySeverity' ? 'vuln.sev' : 'osfam', v);
    return x.charAt(0).toUpperCase() + x.slice(1);
  };
  const barColor = (v: string) =>
    k === 'vulnsBySeverity' ? (({ critical: 'var(--crit)', high: 'var(--warn)', medium: 'var(--info)' }) as Record<string, string>)[v] ?? 'var(--line-strong)' : 'var(--accent)';
  const daily = k === 'alertsPerDay' || k === 'changesPerDay';
  const series = k === 'alertsPerDay' ? ['critical', 'major', 'other'] : ['n'];
  const colors: Record<string, string> = { critical: 'var(--crit)', major: 'var(--warn)', other: 'var(--info, var(--accent))', n: 'var(--accent)' };
  const total = (r: Row) => series.reduce((a, s) => a + Number(r[s] ?? 0), 0);
  const max = Math.max(1, ...rows.map(total));
  const W = 460, H = 140;
  return (
    <div className="panel" style={{ padding: 14 }}>
      <b className="small">{t(`home.chart.${k}` as PlatformKey)}</b>
      {daily ? (
        <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%" role="img" aria-label={t(`home.chart.${k}` as PlatformKey)}>
          {rows.map((r, i) => {
            const bw = W / rows.length;
            let y = H;
            return (
              <g key={i}>
                {series.map((s) => {
                  const h = (Number(r[s] ?? 0) / max) * (H - 6);
                  y -= h;
                  return <rect key={s} x={i * bw + 2} y={y} width={bw - 4} height={h} fill={colors[s]} rx={2} />;
                })}
                {i % 3 === 0 && (
                  <text x={i * bw + bw / 2} y={H + 14} fontSize={10} textAnchor="middle" fill="var(--muted)">
                    {new Date(String(r.day)).toLocaleDateString(lang, { day: '2-digit', month: '2-digit', timeZone: 'UTC' })}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      ) : null}
      {daily && series.length > 1 && (
        <div className="row xs muted" style={{ gap: 12 }}>
          {series.map((x) => (
            <span key={x}>
              <i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: colors[x], marginRight: 4 }} />
              {t(`home.legend.${x}` as PlatformKey)}
            </span>
          ))}
        </div>
      )}
      {daily ? null : (
        <div className="stack-sm" style={{ marginTop: 8 }}>
          {rows.map((r) => (
            <div key={String(r.name)} className="row small" style={{ gap: 8, alignItems: 'center' }}>
              <span style={{ width: 110 }}>{name(String(r.name))}</span>
              <span style={{ flex: 1, background: 'var(--surface-2)', height: 10, borderRadius: 5 }}>
                <span style={{ display: 'block', width: `${(100 * Number(r.n)) / max}%`, height: 10, borderRadius: 5, background: barColor(String(r.name)) }} />
              </span>
              <span style={{ width: 50, textAlign: 'right' }}>{fmt.number(Number(r.n))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** "Personalizar": mostrar/esconder e reordenar cartões e gráficos (fica salvo para a pessoa). */
function Customize({ items, layout, onClose }: { items: { key: string; label: string }[]; layout: Layout; onClose: (saved: boolean) => void }) {
  const t = useT();
  const s = useSession();
  const toast = useToast();
  const start = [...items].sort((a, b) => {
    const ia = (layout.order ?? []).indexOf(a.key), ib = (layout.order ?? []).indexOf(b.key);
    return (ia < 0 ? 1000 : ia) - (ib < 0 ? 1000 : ib);
  });
  const [list, setList] = useState(start);
  const [hidden, setHiddenKeys] = useState<string[]>(layout.hidden ?? []);
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const n = [...list];
    [n[i], n[j]] = [n[j]!, n[i]!];
    setList(n);
  };
  const save = async (reset = false) => {
    try {
      await unwrap(s.api.PUT('/api/v1/home/layout', { body: reset ? { order: [], hidden: [] } : { order: list.map((x) => x.key), hidden } }));
      onClose(true);
    } catch (e) {
      toast(<ProblemBox error={e} />, { kind: 'crit', timeout: 9000 });
    }
  };
  return (
    <Modal
      title={t('home.customize')}
      onClose={() => onClose(false)}
      foot={
        <>
          <button type="button" className="btn btn-ghost" onClick={() => void save(true)}>{t('home.customizeReset')}</button>
          <button type="button" className="btn" onClick={() => onClose(false)}>{t('ui.cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={() => void save()}>{t('home.customizeSave')}</button>
        </>
      }
    >
      <p className="small muted">{t('home.customizeLead')}</p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {list.map((x, i) => (
          <li key={x.key} className="row" style={{ gap: 8, alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--line)' }}>
            <input type="checkbox" aria-label={x.label} checked={!hidden.includes(x.key)} onChange={(e) => setHiddenKeys(e.target.checked ? hidden.filter((k) => k !== x.key) : [...hidden, x.key])} />
            <span style={{ flex: 1 }} className="small">{x.label}</span>
            <button type="button" className="btn btn-sm btn-ghost" aria-label={t('home.up')} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button type="button" className="btn btn-sm btn-ghost" aria-label={t('home.down')} disabled={i === list.length - 1} onClick={() => move(i, 1)}>↓</button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
